'use client';

import { AlertDialog as AlertDialogPrimitive } from '@base-ui/react/alert-dialog';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = ({ className, ...props }: AlertDialogPrimitive.Trigger.Props) => {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" className={className} {...props} />;
};

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogBackdrop = ({ className, ...props }: AlertDialogPrimitive.Backdrop.Props) => {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-backdrop"
      className={cn(
        'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      {...props}
    />
  );
};

// gloss
const AlertDialogViewport = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <AlertDialogPrimitive.Viewport
      data-slot="alert-dialog-viewport"
      className={cn(
        'pointer-events-auto fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        className,
      )}
      {...props}
    />
  );
};

const alertDialogPopupVariants = cva(
  'bg-background text-foreground border-border relative grid gap-4 border p-6 shadow-lg rounded-xl transition-[scale,opacity] duration-150 ease-out data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0',
  {
    variants: {
      size: {
        sm: 'w-full max-w-sm',
        default: 'w-full max-w-md',
        lg: 'w-full max-w-lg',
        full: 'w-full max-w-2xl',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

type AlertDialogPopupProps = AlertDialogPrimitive.Popup.Props & VariantProps<typeof alertDialogPopupVariants>;

const AlertDialogPopup = ({ className, size = 'default', ...props }: AlertDialogPopupProps) => {
  return (
    <AlertDialogPrimitive.Popup
      data-slot="alert-dialog-popup"
      className={cn(alertDialogPopupVariants({ size, className }))}
      {...props}
    />
  );
};

const AlertDialogHeader = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
};

const AlertDialogFooter = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

const AlertDialogTitle = ({ className, ...props }: AlertDialogPrimitive.Title.Props) => {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn('font-semibold text-lg leading-none tracking-tight', className)}
      {...props}
    />
  );
};

const AlertDialogDescription = ({ className, ...props }: AlertDialogPrimitive.Description.Props) => {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

const AlertDialogClose = (props: AlertDialogPrimitive.Close.Props) => {
  return <AlertDialogPrimitive.Close data-slot="alert-dialog-close" {...props} />;
};

export type { AlertDialogPopupProps };
export {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogViewport,
  alertDialogPopupVariants,
};
